import { Box, List, Row, Text, Title, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  ExpensesSettingsPageDocument,
  ToggleExpensesGroupsSettingDocument,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import Upgrade, { UpgradeActions } from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface Props {
  hasExpensesGroupsFeature: boolean
  isExpensesGroupsEnabled: boolean
  hideTitle?: boolean
}

const SectionUnavailable = ({ showUpgradeModal }: { showUpgradeModal: () => void }) => {
  return (
    <Upgrade.Small
      title={i18n.t('expenses_v2.settings.expenses_groups.upgrade_title')}
      action={{
        type: UpgradeActions.knowMore,
        onClick: () => showUpgradeModal(),
      }}
    />
  )
}

const SectionAvailable = ({
  isExpensesGroupsEnabled,
  toggleExpensesGroups,
}: {
  isExpensesGroupsEnabled: boolean
  toggleExpensesGroups: () => void
}) => {
  return (
    <List>
      <List.Item>
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="baseline" width="full">
          <Box gap="s12" paddingRight="s12">
            <Text.Normal weight="medium" size="300">
              {i18n.t('expenses_v2.settings.expenses_groups.content.title')}
            </Text.Normal>
            <Text.Normal weight="normal" size="200" color="grey700">
              {i18n.t('expenses_v2.settings.expenses_groups.content.description')}
            </Text.Normal>
          </Box>
          <TogglerCheckbox
            value={isExpensesGroupsEnabled}
            onChange={() => toggleExpensesGroups()}
          />
        </Box>
      </List.Item>
    </List>
  )
}

const ExpensesGroupsSection = ({
  hasExpensesGroupsFeature,
  isExpensesGroupsEnabled,
  hideTitle = false,
}: Props) => {
  const { company } = useCurrentSession()
  const [upgradeModal, showUpgradeModal] = useToggle()

  const [toggleExpensesGroupsSetting] = useMutation(ToggleExpensesGroupsSettingDocument, {
    extractorFn: (data) => data.expensesMutations.toggleExpensesExpensableGroupsSetting,
  })

  const handleToggleExpensesGroupsSetting = async () => {
    const result = await toggleExpensesGroupsSetting({
      variables: { companyId: company.id },
      refetchQueries: [ExpensesSettingsPageDocument],
    })

    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  return (
    <>
      <Box flexGrow>
        <Row>
          {!hideTitle && (
            <Row.Col halfWidth>
              <Title
                variant="400"
                title={i18n.t('expenses_v2.settings.expenses_groups.title')}
                description={i18n.t('expenses_v2.settings.expenses_groups.description')}
                illustration="group"
              />
            </Row.Col>
          )}
          <Row.Col halfWidth>
            {hasExpensesGroupsFeature ? (
              <SectionAvailable
                isExpensesGroupsEnabled={isExpensesGroupsEnabled}
                toggleExpensesGroups={handleToggleExpensesGroupsSetting}
              />
            ) : (
              <SectionUnavailable showUpgradeModal={showUpgradeModal} />
            )}
          </Row.Col>
        </Row>
      </Box>
      {upgradeModal && (
        <UpsellingKit.ProductModal
          isOpen={upgradeModal}
          onClose={showUpgradeModal}
          requestSource="Expenses settings page"
          featureRequested={Features.expenses_report_on_behalf}
          moduleRequested={AvailableModules.expenses}
          requestReason={missingFeatureRequestReason(company)}
          pageSource="expenses_settings_groups"
        />
      )}
    </>
  )
}

export default ExpensesGroupsSection
